// src/screens/WishlistScreen.jsx - FIXED VERSION
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Heart, Trash2, ShoppingBag, Gift, Sparkles, RefreshCw, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import MainFooter from '../components/MainFooter';
import API_URL from '../config/api';

const WishlistScreen = ({ 
  user, 
  onLogout, 
  currentScreen, 
  onNavigate, 
  products, 
  wishlist, 
  toggleWishlist,
  onProductSelect,
  wishlistLoading 
}) => {
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [similarError, setSimilarError] = useState(null);

  // Get full product data for wishlist items
  const wishlistProducts = products.filter(p => wishlist.includes(p.asin));

  // Fetch similar products from backend
  const fetchSimilarProducts = useCallback(async () => {
    if (wishlistProducts.length === 0) {
      setSimilarProducts([]);
      return;
    }

    setLoadingSimilar(true);
    setSimilarError(null);

    try {
      const response = await fetch(`${API_URL}/recommend/similar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wishlist_asins: wishlist,
          exclude_asins: wishlist,
          top_k: 8
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch similar products');
      }

      const data = await response.json();
      // FIXED: Access recommendations from the response object
      console.log('Similar products response:', data);
      setSimilarProducts(data.recommendations || []);
    } catch (error) {
      console.error('Error fetching similar products:', error);
      setSimilarError('Unable to load recommendations');
      // Fallback to random products not in wishlist
      const fallback = products
        .filter(p => !wishlist.includes(p.asin))
        .sort(() => Math.random() - 0.5)
        .slice(0, 8);
      setSimilarProducts(fallback);
    } finally {
      setLoadingSimilar(false);
    }
  }, [wishlist, wishlistProducts.length, products]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Fetch similar products when wishlist changes
  useEffect(() => {
    if (!wishlistLoading && wishlistProducts.length > 0) {
      fetchSimilarProducts();
    } else if (wishlistProducts.length === 0) {
      setSimilarProducts([]);
    }
  }, [wishlist, wishlistLoading, wishlistProducts.length, fetchSimilarProducts]);

  const similarScrollRef = useRef(null);

  return (
    <div className="min-h-screen bg-[#FBFCF6]">
      <Header 
        user={user} 
        onLogout={onLogout} 
        currentScreen={currentScreen}
        onNavigate={onNavigate}
      />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-12 relative z-10">
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2 sm:gap-3">
            <Heart className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#06402B]" />
            My Wishlist
          </h1>
          <p className="text-slate-600 mt-1 sm:mt-2 text-sm sm:text-base">
            {wishlistProducts.length} {wishlistProducts.length === 1 ? 'item' : 'items'} saved
          </p>
        </div>

        {wishlistLoading ? (
          <div className="text-center py-12 sm:py-16">
            <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#06402B] border-t-transparent rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
            <p className="text-slate-600 text-sm sm:text-base">Loading your wishlist...</p>
          </div>
        ) : wishlistProducts.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12 sm:py-16">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2">Your wishlist is empty</h2>
            <p className="text-slate-600 mb-4 sm:mb-6 max-w-md mx-auto text-sm sm:text-base px-4">
              Start adding items you love by clicking the heart icon on any product.
            </p>
            <button
              onClick={(e) => {
                e.preventDefault();
                onNavigate('home');
              }}
              type="button"
              className="inline-flex items-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 bg-[#06402B] text-white rounded-xl sm:rounded-[5px] hover:bg-[#053527] transition-colors font-semibold text-sm sm:text-base active:scale-95"
            >
              <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
              Browse Products
            </button>
          </div>
        ) : (
          /* Wishlist Grid - Mobile optimized: 2 columns */
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {wishlistProducts.map((product) => (
              <div 
                key={product.asin}
                className="bg-white relative rounded-xl sm:rounded-[5px] overflow-hidden shadow-sm hover:shadow-lg transition-all group"
              >
                <div className="relative aspect-square bg-white">
                  <img 
                    src={product.image_urls?.[0]} 
                    alt={product.title}
                    className="w-full h-full object-contain p-3 sm:p-4"
                    loading="lazy"
                  />
                  {/* Remove from Wishlist Button - Always visible on mobile */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleWishlist(product.asin);
                    }}
                    type="button"
                    className="absolute top-2 right-2 sm:top-3 sm:right-3 w-10 h-8 sm:w-10 sm:h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-red-50 transition-colors active:scale-95"
                    title="Remove from wishlist"
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 hover:text-red-500 transition-colors" />
                  </button>
                </div>
                
                <div className="p-3 sm:p-4">
                  <h3 className="font-semibold text-slate-800 text-sm sm:text-base line-clamp-2 mb-2 sm:mb-3 min-h-[2.5rem] sm:min-h-[3rem]">
                    {product.title}
                  </h3>
                  
                  {/* Price display - FIXED */}
                  {product.price_display && (
                    <div className="text-[#06402B] font-bold text-base sm:text-lg mb-2">
                      {product.price_display}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        onProductSelect(product);
                      }}
                      type="button"
                      className="flex-1 py-2 sm:py-2.5 border-2 border-[#06402B] text-[#06402B] rounded-lg sm:rounded-[5px] hover:bg-[#06402B]/5 transition-colors font-medium text-xs sm:text-sm active:scale-95"
                    >
                      View Details
                    </button>
                    {product.asin && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            product.affiliate_url || `https://www.amazon.com/dp/${product.asin}?tag=surpriseai01-20`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                        type="button"
                        className="w-10 h-auto sm:px-3 sm:py-2 bg-[#06402B] text-white rounded-lg sm:rounded-[5px] hover:bg-[#053527] transition-colors flex items-center justify-center active:scale-95"
                        title="Buy Now"
                      >
                        <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AI-Powered Similar Products Section */}
        {wishlistProducts.length > 0 && (
          <div className="mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8 border-t border-slate-200">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-[#06402B]" />
                  Recommended For You
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1">
                  Based on your wishlist
                </p>
              </div>
              
              {/* Refresh Button */}
              <button
                onClick={fetchSimilarProducts}
                disabled={loadingSimilar}
                type="button"
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-[#06402B] hover:bg-[#06402B]/5 rounded-lg transition-colors disabled:opacity-50 active:scale-95"
              >
                <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${loadingSimilar ? 'animate-spin' : ''}`} />
                <span className="hidden xs:inline">Refresh</span>
              </button>
            </div>

            {/* Loading State */}
            {loadingSimilar && (
              <div className="flex items-center justify-center py-8 sm:py-12">
                <div className="text-center">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 border-3 border-[#06402B] border-t-transparent rounded-full animate-spin mx-auto mb-2 sm:mb-3"></div>
                  <p className="text-xs sm:text-sm text-slate-500">Finding similar gifts...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {similarError && !loadingSimilar && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <p className="text-xs sm:text-sm text-amber-700">{similarError}. Showing popular alternatives.</p>
              </div>
            )}

            {/* Similar Products - Horizontal scroll on mobile, grid on desktop */}
            {!loadingSimilar && similarProducts.length > 0 && (
              <div 
                ref={similarScrollRef}
                className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-4 md:overflow-visible md:pb-0"
                style={{ 
                  scrollbarWidth: 'none', 
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {similarProducts.map((product) => (
                  <div 
                    key={product.asin}
                    className="flex-shrink-0 w-[45%] sm:w-[40%] md:w-full snap-start bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group"
                    onClick={(e) => {
                      e.preventDefault();
                      onProductSelect(product);
                    }}
                  >
                    <div className="relative aspect-square bg-[#FBFCF6]">
                      <img 
                        src={product.image_urls?.[0]} 
                        alt={product.title}
                        className="w-full h-full object-contain p-2 sm:p-3 group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      
                      {/* Similarity Score Badge */}
                      {product.similarity_score && (
                        <div className="absolute top-1.5 left-1.5 sm:top-2 sm:left-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-[#06402B] text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center gap-0.5 sm:gap-1">
                          <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          <span className="hidden xs:inline">{Math.round(product.similarity_score * 100)}% Match</span>
                          <span className="xs:hidden">{Math.round(product.similarity_score * 100)}%</span>
                        </div>
                      )}
                      
                      {/* Add to Wishlist Button - Always visible on mobile */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleWishlist(product.asin);
                        }}
                        type="button"
                        className={`absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-10 h-8 rounded-full flex items-center justify-center shadow-md transition-all active:scale-95 ${
                          wishlist.includes(product.asin)
                            ? 'bg-red-50'
                            : 'bg-white/90 backdrop-blur-sm md:opacity-0 md:group-hover:opacity-100'
                        }`}
                      >
                        <Heart 
                          className={`w-4 h-4 sm:w-4 sm:h-4 transition-colors ${
                            wishlist.includes(product.asin)
                              ? 'fill-red-500 text-red-500'
                              : 'text-slate-600'
                          }`} 
                        />
                      </button>
                    </div>
                    
                    <div className="p-2.5 sm:p-3">
                      <h3 className="font-medium text-slate-800 line-clamp-2 text-xs sm:text-sm mb-1.5 sm:mb-2 min-h-[2rem] sm:min-h-[2.5rem]">
                        {product.title}
                      </h3>
                      
                      {/* Price display - FIXED */}
                      {product.price_display && (
                        <div className="text-[#06402B] font-bold text-sm sm:text-base mb-2">
                          {product.price_display}
                        </div>
                      )}
                      
                      {/* Recommendation Reason - Hidden on small mobile */}
                      {product.recommendation_reason && (
                        <p className="hidden sm:block text-xs text-[#06402B] line-clamp-1 mb-2">
                          {product.recommendation_reason}
                        </p>
                      )}
                      
                      {/* View Details Link */}
                      <div className="flex items-center text-[10px] sm:text-xs text-[#06402B] font-medium group-hover:underline">
                        View Details
                        <ChevronRight className="w-3 h-3 ml-0.5" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Recommendations State */}
            {!loadingSimilar && similarProducts.length === 0 && !similarError && (
              <div className="text-center py-6 sm:py-8">
                <Gift className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-2 sm:mb-3" />
                <p className="text-slate-500 text-sm sm:text-base">Add more items to get personalized recommendations</p>
              </div>
            )}
          </div>
        )}
      </div>

      <MainFooter />

      {/* Hide scrollbar CSS */}
      <style>{`
        div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default WishlistScreen;